/**
 * BriefDetailView
 *
 * The SMM's full-page brief workspace (route ``briefs/:briefId``), laid out
 * as the same WorkspacePanel cockpit as the idea detail page (DD-014):
 * Concept panel on the left carries the brief's own work — title/property
 * subheader (DD-010 language), the DRAFT -> CAPTURING -> EDITING -> COMPLETE
 * batch-progress rail, AI distilled guidance, notes, and the "Link fotografo"
 * share action; the Ideas panel on the right lists each linked idea as a
 * flat hairline-divided row (DD-002) with its live pipeline position and a
 * click-through into the idea workspace page.
 *
 * Chrome principle: this page is for the SMM. The client/creator review stays
 * the existing modal + magic-link flow, untouched here.
 */

import React, { useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { copyToClipboard } from "@/utils/clipboard";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { PageLayout } from "@/components/PageLayout";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import {
  BoltIcon,
  SpinnerIcon,
  SendIcon,
  EditIcon,
  TrashIcon,
  PlusIcon,
} from "@/components/icons";
import { IdeaConceptDisplay } from "@/domains/Ideas/components/IdeaConceptDisplay";
import { getIdeaConcept } from "@/domains/Ideas/ideaHelpers";
import { useAppStore } from "@/core/store/useAppStore";
import {
  useBriefQuery,
  useBriefNotesQuery,
  useAddIdeaToBriefMutation,
  useRemoveIdeaFromBriefMutation,
  useAddBriefNoteMutation,
  useUpdateBriefNoteMutation,
  useDeleteBriefNoteMutation,
  useShareBriefMutation,
  useUpdateBriefMutation,
  useGenerateBriefContextMutation,
} from "./hooks/useBriefs";
import { useIdeasQuery } from "../Ideas/hooks/useIdeas";
import { IdeaStatusBadge } from "../Ideas/components/IdeaStatusBadge";
import { BriefNote, BriefShareLink, BriefStatus } from "./types";
import { Idea } from "../Ideas/types";
import { BriefStatusBadge } from "./components/BriefStatusBadge";
import { BriefStageRail } from "./components/BriefStageRail";
import { BriefIdeaRow } from "./components/BriefIdeaRow";
import { TaskListEditor } from "@/domains/Tasks/components/TaskListEditor";
import { TaskDrawer } from "@/domains/Tasks/drawer/TaskDrawer";
import { viaItemPath } from "@/domains/Tasks/drawer/taskLinks";
import { useTaskDrawer } from "@/domains/Tasks/drawer/useTaskDrawer";
import { capabilitiesFor } from "@/domains/Tasks/taskCapabilities";
import { taskListProgress } from "@/domains/Tasks/taskList";
import { EMPTY_TASK_LIST } from "@/domains/Tasks/types";
import { useShotListQuery, useToggleTask } from "@/domains/Tasks/hooks/useTasks";
import {
  CATEGORY_COLORS,
  formatDateRange,
} from "@/domains/Home/components/CalendarEventCard";


export const BriefDetailView: React.FC = () => {
  const { briefId } = useParams<{ briefId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id;

  const { data: brief, isLoading, error } = useBriefQuery(briefId);

  // Header trail: returns to wherever the brief was opened from; falls back
  // to the briefs list (parent path) on deep link / refresh.
  const { goBack, navOptions } = useDetailTrail({
    fallback: {
      to: location.pathname.replace(/\/[^/]+$/, ""),
      label: ENTITY_CONFIG.brief.plural,
    },
    title: brief?.title?.trim() || "Untitled Brief",
  });

  const [newNoteContent, setNewNoteContent] = useState("");
  const [showAddIdeaModal, setShowAddIdeaModal] = useState(false);
  // Both workspace panels open by default; collapse what you aren't using.
  const [collapsed, setCollapsed] = useState<
    Record<"concept" | "ideas" | "shots", boolean>
  >({
    concept: false,
    ideas: false,
    shots: false,
  });
  const toggle = (id: "concept" | "ideas" | "shots") =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const [shareLink, setShareLink] = useState<BriefShareLink | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const { data: notes = [] } = useBriefNotesQuery(briefId);
  // All ideas (any status) are linkable to a brief, not just approved ones.
  const { data: allIdeas = [] } = useIdeasQuery(clientId, undefined, false, false);

  const addIdeaMutation = useAddIdeaToBriefMutation(clientId || "");
  const removeIdeaMutation = useRemoveIdeaFromBriefMutation(clientId || "");
  const addNoteMutation = useAddBriefNoteMutation(briefId || "");
  const updateNoteMutation = useUpdateBriefNoteMutation(briefId || "");
  const deleteNoteMutation = useDeleteBriefNoteMutation(briefId || "");
  const shareMutation = useShareBriefMutation(clientId || "");
  const updateMutation = useUpdateBriefMutation(clientId || "");
  const generateContextMutation = useGenerateBriefContextMutation(clientId || "");

  // Shot list (DD-022, DD-052): tasks linked to the brief, then a via group
  // per idea and per linked event. A row opens the task drawer; a group
  // header opens its item with the brief stamped into the back trail.
  const { data: shotList } = useShotListQuery(briefId);
  const toggleTask = useToggleTask();
  const taskDrawer = useTaskDrawer();
  const shotProgress = taskListProgress(shotList ?? EMPTY_TASK_LIST);
  const taskCapabilities = capabilitiesFor("admin");

  const availableIdeas = allIdeas.filter(
    (idea) => !brief?.ideas?.some((bi) => bi.id === idea.id)
  );

  const handleAddIdea = async (ideaId: string) => {
    if (!briefId) return;
    await addIdeaMutation.mutateAsync({ briefId, ideaId });
    setShowAddIdeaModal(false);
  };

  const handleRemoveIdea = async (ideaId: string) => {
    if (!briefId) return;
    await removeIdeaMutation.mutateAsync({ briefId, ideaId });
  };

  const handleStatusChange = (status: BriefStatus) => {
    if (!briefId || !brief || status === brief.status) return;
    updateMutation.mutate({ briefId, data: { status } });
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    await addNoteMutation.mutateAsync({ content: newNoteContent.trim() });
    setNewNoteContent("");
  };

  const handleTitleSave = async () => {
    if (!briefId || editingTitle === null) return;
    const trimmed = editingTitle.trim();
    if (trimmed && trimmed !== brief?.title) {
      await updateMutation.mutateAsync({ briefId, data: { title: trimmed } });
    }
    setEditingTitle(null);
  };

  const handleStartEditNote = (note: BriefNote) => {
    setEditingNoteId(note.id);
    setEditNoteContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteContent("");
  };

  const handleSaveEditNote = async () => {
    if (!editingNoteId || !editNoteContent.trim()) return;
    await updateNoteMutation.mutateAsync({
      noteId: editingNoteId,
      data: { content: editNoteContent.trim() },
    });
    handleCancelEditNote();
  };

  const handleDeleteNoteConfirm = async () => {
    if (!noteToDelete) return;
    const id = noteToDelete;
    setNoteToDelete(null);
    await deleteNoteMutation.mutateAsync(id);
  };

  const handleShare = async () => {
    if (!briefId) return;
    const result = await shareMutation.mutateAsync(briefId);
    setShareLink(result);
  };

  const handleCopyShareUrl = () => {
    if (shareLink) copyToClipboard(shareLink.magicLink);
  };

  const formatExpiry = (iso: string): string =>
    new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Client Selected
          </h2>
          <p className="text-gray-500">Please select a client first</p>
        </div>
      </div>
    );
  }

  const isValidBriefId = briefId && briefId !== "null" && briefId !== "undefined";

  if (!isValidBriefId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            Invalid Brief
          </h2>
          <p className="text-gray-500 mb-4">No valid brief ID provided</p>
          <button
            onClick={goBack}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Briefs
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-gray-500">Loading brief...</div>
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-red-600 mb-2">
            Error Loading Brief
          </h2>
          <p className="text-gray-500 mb-4">
            {error instanceof Error ? error.message : "Brief not found"}
          </p>
          <button
            onClick={goBack}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Briefs
          </button>
        </div>
      </div>
    );
  }

  // Ideas can be linked/unlinked at any point in the brief's lifecycle.
  const canManageIdeas = true;
  const ideaCount = brief.ideas?.length || 0;

  return (
    <PageLayout>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Cockpit — Concept left, linked Ideas right, mirroring the idea
            detail page (DD-010/DD-011). Below xl the whole cockpit scrolls;
            from xl up each column scrolls on its own. */}
        <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden px-4 lg:px-6 py-5">
          <div className="flex flex-col xl:flex-row gap-5 items-start xl:h-full">
            {/* Concept — the brief's own work: stage rail, AI guidance, notes. */}
            <WorkspacePanel
              title="Concept"
              icon={<ENTITY_CONFIG.brief.icon className="w-4 h-4" />}
              collapsed={collapsed.concept}
              onToggleCollapse={() => toggle("concept")}
              className="w-full xl:w-[38%] xl:max-w-xl xl:max-h-full"
              actions={
                // Always available regardless of brief status (DD-023).
                <PanelHeaderAction
                  label={shareMutation.isPending ? "Generazione..." : "Link fotografo"}
                  icon={<SendIcon className="w-4 h-4" />}
                  onClick={handleShare}
                  disabled={shareMutation.isPending}
                />
              }
              subheader={
                // Brief header — title + property strip, fixed with the panel
                // header while the body scrolls (DD-010).
                <div className="space-y-3 pt-1">
                  {editingTitle !== null ? (
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={handleTitleSave}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleTitleSave();
                        }
                        if (e.key === "Escape") setEditingTitle(null);
                      }}
                      className="text-xl lg:text-2xl font-bold tracking-tight text-gray-900 border-b-2 border-blue-500 focus:outline-hidden w-full bg-transparent leading-tight"
                      placeholder="Brief title"
                      autoFocus
                    />
                  ) : (
                    <h2
                      className="text-xl lg:text-2xl font-bold tracking-tight text-gray-900 cursor-text hover:text-gray-700 transition-colors leading-tight"
                      onClick={() => setEditingTitle(brief.title || "")}
                      title="Click to edit"
                    >
                      {brief.title || "Untitled Brief"}
                    </h2>
                  )}
                  <dl className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <dd>
                      <BriefStatusBadge
                        status={brief.status}
                        onStatusChange={handleStatusChange}
                        disabled={updateMutation.isPending}
                      />
                    </dd>
                    <dd className="text-xs font-medium text-gray-400 whitespace-nowrap">
                      {ideaCount} {ideaCount === 1 ? "idea" : "ideas"}
                    </dd>
                    {updateMutation.isPending && (
                      <dd className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                        <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                        Saving…
                      </dd>
                    )}
                  </dl>
                </div>
              }
            >
              <div className="space-y-5">
                {shareLink && (
                  <div className="p-3 bg-green-50 border border-green-100 rounded-lg space-y-2">
                    <div className="flex flex-col min-w-0">
                      <span className="text-green-800 text-sm font-medium">
                        Link magico generato. Invialo al fotografo per la revisione.
                      </span>
                      <span className="text-xs text-green-700">
                        Scade il {formatExpiry(shareLink.expiresAt)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={shareLink.magicLink}
                        className="flex-1 min-w-0 px-3 py-1 text-sm border border-green-300 rounded-sm bg-white"
                      />
                      <button
                        onClick={handleCopyShareUrl}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        Copia
                      </button>
                      <button
                        onClick={() => setShareLink(null)}
                        className="px-3 py-1 text-sm text-green-600 hover:text-green-800"
                      >
                        Chiudi
                      </button>
                    </div>
                  </div>
                )}

                {/* Batch-progress pipeline. */}
                <BriefStageRail
                  status={brief.status}
                  onStatusChange={handleStatusChange}
                  disabled={updateMutation.isPending}
                />

                {/* Attached calendar events — same tinted rows as BriefCard. */}
                {(brief.calendarEvents?.length ?? 0) > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
                      Events
                    </div>
                    <div className="flex flex-col gap-1">
                      {brief.calendarEvents?.map((event) => {
                        const dateLabel = formatDateRange(event);
                        const categoryStyle =
                          CATEGORY_COLORS[event.category] ||
                          "bg-gray-50 border-gray-200 text-gray-800";
                        return (
                          <div
                            key={event.id}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md border text-[11px] ${categoryStyle}`}
                          >
                            {dateLabel && (
                              <span className="font-medium shrink-0">{dateLabel}</span>
                            )}
                            <span className="font-semibold truncate">
                              {event.eventName}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* AI guidance */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] flex items-center gap-1.5">
                      <BoltIcon className="w-3.5 h-3.5 text-amber-500" />
                      AI Guidance
                    </div>
                    <button
                      onClick={() => briefId && generateContextMutation.mutate(briefId)}
                      disabled={generateContextMutation.isPending}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {generateContextMutation.isPending ? (
                        <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                      ) : null}
                      {brief.distilledContext ? "Rigenera" : "Genera"}
                    </button>
                  </div>
                {brief.distilledContext ? (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {brief.distilledContext}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No guidance yet. Generate distilled direction for the
                    creator from this brief's ideas.
                  </p>
                )}
              </div>

                {/* Notes */}
                <div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
                    Notes
                  </div>

                {notes.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {notes.map((note: BriefNote) => (
                      <div key={note.id} className="group p-3 bg-gray-50 rounded-sm">
                        {editingNoteId === note.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editNoteContent}
                              onChange={(e) => setEditNoteContent(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                              rows={2}
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={handleCancelEditNote}
                                className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-sm"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveEditNote}
                                disabled={
                                  !editNoteContent.trim() ||
                                  updateNoteMutation.isPending
                                }
                                className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-50"
                              >
                                {updateNoteMutation.isPending ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-gray-800 whitespace-pre-wrap min-w-0 flex-1">
                                {note.content}
                              </p>
                              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleStartEditNote(note)}
                                  className="p-1 text-gray-400 hover:text-gray-700"
                                  aria-label="Edit note"
                                >
                                  <EditIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setNoteToDelete(note.id)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  aria-label="Delete note"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-400">
                                {new Date(note.createdAt).toLocaleString()}
                              </span>
                              {note.tags.length > 0 && (
                                <div className="flex gap-1">
                                  {note.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-sm"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mb-4">No notes yet.</p>
                )}

                <div className="flex gap-2">
                  <textarea
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a note..."
                    rows={2}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteContent.trim() || addNoteMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 self-end"
                  >
                    Add
                  </button>
                </div>
                </div>
              </div>
            </WorkspacePanel>

            {/* Linked ideas — flat rows with hairline dividers (DD-002). */}
            <div className="w-full xl:flex-1 flex flex-col gap-5 min-w-0 xl:max-h-full xl:overflow-y-auto">
              <WorkspacePanel
                title={ENTITY_CONFIG.idea.plural}
                icon={<ENTITY_CONFIG.idea.icon className="w-4 h-4" />}
                summary={ideaCount > 0 ? `${ideaCount} linked` : "none yet"}
                collapsed={collapsed.ideas}
                onToggleCollapse={() => toggle("ideas")}
                actions={
                  canManageIdeas ? (
                    <PanelHeaderAction
                      label="Add Ideas"
                      icon={<PlusIcon className="w-4 h-4" />}
                      onClick={() => setShowAddIdeaModal(true)}
                    />
                  ) : undefined
                }
              >
                {brief.ideas && brief.ideas.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {brief.ideas.map((idea) => (
                      <BriefIdeaRow
                        key={idea.id}
                        idea={idea}
                        clientId={clientId || ""}
                        trailNavOptions={navOptions}
                        onRemoveFromBrief={
                          canManageIdeas
                            ? () => handleRemoveIdea(idea.id)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <ENTITY_CONFIG.idea.icon className="w-9 h-9 text-gray-300 mb-2.5" />
                    <p className="text-sm text-gray-500">No ideas linked yet.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add the ideas this shoot should cover.
                    </p>
                  </div>
                )}
              </WorkspacePanel>

              <WorkspacePanel
                title="Shot list"
                icon={<ENTITY_CONFIG.task.icon className="w-4 h-4" />}
                summary={
                  shotProgress.total > 0
                    ? `${shotProgress.done}/${shotProgress.total} done`
                    : "none yet"
                }
                collapsed={collapsed.shots}
                onToggleCollapse={() => toggle("shots")}
                actions={
                  <PanelHeaderAction
                    label={ENTITY_CONFIG.task.createLabel}
                    icon={<PlusIcon className="w-4 h-4" />}
                    onClick={() =>
                      taskDrawer.create({
                        clientId: brief.clientId,
                        links: [{ type: "brief", targetId: brief.id }],
                      })
                    }
                  />
                }
              >
                <TaskListEditor
                  list={shotList ?? EMPTY_TASK_LIST}
                  capabilities={taskCapabilities}
                  onToggle={toggleTask}
                  onOpen={taskDrawer.open}
                  onOpenItem={(item) => navigate(viaItemPath(item, brief.clientId), navOptions)}
                  emptyHint="No photographer or videographer tasks yet"
                />
              </WorkspacePanel>
            </div>
          </div>
        </div>
      </div>

      {/* Add Ideas Modal */}
      <Modal
        isOpen={showAddIdeaModal}
        onClose={() => setShowAddIdeaModal(false)}
        title="Add Ideas"
        size="lg"
        bodyPadding
        footer={
          <button
            onClick={() => setShowAddIdeaModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        }
      >
        {availableIdeas.length > 0 ? (
          <div className="space-y-2">
            {availableIdeas.map((idea: Idea) => (
              <button
                key={idea.id}
                onClick={() => handleAddIdea(idea.id)}
                className="w-full text-left p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-medium text-gray-900 truncate">
                    {idea.title}
                  </h4>
                  <IdeaStatusBadge status={idea.status} />
                </div>
                <IdeaConceptDisplay
                  concept={getIdeaConcept(idea)}
                  variant="preview"
                  maxLength={100}
                  className="text-sm text-gray-600 truncate"
                />
              </button>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            No ideas available to add. Create an idea first.
          </p>
        )}
      </Modal>

      <TaskDrawer
        drawer={taskDrawer}
        capabilities={taskCapabilities}
        navOptions={navOptions}
        excludeClientContacts
      />

      {/* Note delete confirmation */}
      <ConfirmDialog
        isOpen={noteToDelete !== null}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleDeleteNoteConfirm}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteNoteMutation.isPending}
      />
    </PageLayout>
  );
};
